export type SystemSizeParams = {
  panels?: Record<string, { isActive: boolean | null }>
  installAreasToPanels?: Array<[string, string[]]>
  solarModule: { wattage?: number | null } | null
}

export type DesignSystemSizeParams = SystemSizeParams & {
  source?: `aurora` | `vision` | null
  systemSizeKw?: number | null
}

export const getActivePanelsEntries = <
  P extends { isActive: boolean | null },
>(design: {
  panels: Record<string, P>
  installAreasToPanels: Array<[string, string[]]>
}): [string, P][] =>
  Object.entries(design.panels).filter(
    ([panelId, panel]) =>
      panel.isActive &&
      design.installAreasToPanels.some(([, panelIds]) =>
        panelIds.includes(panelId),
      ),
  )

export const getSystemSizeInWatts = (design: SystemSizeParams): number => {
  const { solarModule } = design
  if (!solarModule) {
    return 0
  }
  if (typeof solarModule?.wattage !== `number`) {
    console.warn(
      `No wattage found for solar module ${JSON.stringify(solarModule)}`,
    )

    return 0
  }
  if (!design.panels) {
    return 0
  }
  if (!design.installAreasToPanels) {
    return 0
  }
  if (typeof design.panels === `object` && design.panels !== null) {
    const activePanelsEntries = getActivePanelsEntries({
      panels: design.panels,
      installAreasToPanels: design.installAreasToPanels,
    })

    return solarModule.wattage * activePanelsEntries.length
  }
  throw new Error(`Invalid panels: ${JSON.stringify(design.panels)}`)
}

export const getDesignSystemSizeInWatts = (
  design: DesignSystemSizeParams,
): number => {
  if (design.source === `aurora`) {
    return typeof design.systemSizeKw === `number` &&
      Number.isFinite(design.systemSizeKw) &&
      design.systemSizeKw > 0
      ? design.systemSizeKw * 1000
      : 0
  }

  return getSystemSizeInWatts(design)
}
